import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import CardSelect from '../components/ui/CardSelect';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Button from '../components/ui/Button';
import {
  CriteriaSchedule,
  DecisionStops,
  LensList,
  PillarClose,
} from '../components/ui/PillarBlocks';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../components/ui/schema';

const BuiltEnvironmentInvestments = () => {
  const { t, i18n } = useTranslation('cy');
  const meta = t('built.meta', { returnObjects: true });
  const [situation, setSituation] = useState(null);
  const faqItems = t('built.faq.items', { returnObjects: true });
  const situations = t('built.situations.items', { returnObjects: true });
  const sectors = t('built.focus.sectors', { returnObjects: true });

  const valueItems = t('built.value.rows', { returnObjects: true }).map((r) => ({
    title: r.topic,
    fields: [
      { label: t('ui.cols.inputShort'), value: r.input },
      { label: t('ui.cols.action'), value: r.action },
      { label: t('ui.cols.measure'), value: r.measure },
    ],
  }));

  const schema = [
    serviceSchema(t('nav.pillars.built.label'), meta.description),
    faqSchema(faqItems),
    breadcrumbSchema(
      [{ label: t('nav.home'), to: '/' }, { label: t('nav.pillars.built.label'), to: '/built-environment-investments' }],
      i18n.language
    ),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/built-environment-investments" schema={schema} />

      <PageHero
        id="built-hero"
        eyebrow={t('built.hero.eyebrow')}
        title={t('built.hero.h1')}
        kicker={t('built.hero.kicker')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.pillars.built.label') }]} />}
      >
        <Notice variant="inline" className="cy-hero__boundary">
          {t('built.hero.boundary')} {t('built.hero.roleNote')}
        </Notice>
        <HeroCtas
          primary={t('built.hero.cta')}
          primaryTo="/submit-opportunity?type=business"
          secondary={t('built.hero.seeCriteria')}
          secondaryTo="#built-focus"
        />
      </PageHero>

      <section className="cy-section cy-section--alt cy-zone--doc" id="built-focus">
        <div className="cy-container">
          <SectionHead title={t('built.focus.h2')} lead={t('built.focus.lead')} />
          <dl className="cy-role-dl">
            {sectors.map((s) => (
              <div className="cy-role-dl__row" key={s.title}>
                <dt>{s.title}</dt>
                <dd>{s.body}</dd>
              </div>
            ))}
          </dl>
          <h3 className="cy-card-title cy-section-note">{t('built.focus.traitsTitle')}</h3>
          <CriteriaSchedule
            rows={t('built.focus.traits', { returnObjects: true })}
            term="dim"
            primary="what"
            secondary="verify"
          />
          <p className="cy-body cy-section-note">{t('built.focus.note')}</p>
          <p className="cy-body cy-section-note">
            {t('built.focus.gateNote')}{' '}
            <Link className="cy-link" to="/approach#approach-gates">{t('built.focus.gateLink')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="built-not-fit">
        <div className="cy-container">
          <SectionHead title={t('built.notFit.h2')} lead={t('built.notFit.lead')} />
          <DecisionStops items={t('built.notFit.items', { returnObjects: true })} featured />
          <Notice variant="compliance" className="cy-notice--inline cy-section-note">
            {t('built.notFit.boundary')} {t('built.notFit.dealBoundary')}
          </Notice>
          <p className="cy-section-action">
            <Link className="cy-link" to="/#home-pillars">{t('built.notFit.alt')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="built-situations">
        <div className="cy-container">
          <SectionHead title={t('built.situations.h2')} lead={t('built.situations.lead')} />
          <CardSelect
            name="situation"
            columns={2}
            value={situation}
            onChange={setSituation}
            options={situations.map((s) => ({
              value: s.title,
              title: s.title,
              desc: s.what,
              summary: s.boundary,
            }))}
          />
          {situation && (
            <div className="cy-section-action">
              <Button to={`/submit-opportunity?type=business&situation=${encodeURIComponent(situation)}`} variant="secondary">
                {t('built.situations.continue')}
              </Button>
            </div>
          )}
          <p className="cy-fineprint cy-section-note">{t('built.situations.note')}</p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="built-structures-governance">
        <div className="cy-container">
          <SectionHead title={t('built.governance.h2')} lead={t('built.governance.lead')} />
          <CriteriaSchedule
            rows={t('built.governance.rows', { returnObjects: true })}
            term="theme"
            primary="preference"
            secondary="variable"
          />
          <Notice variant="compliance" className="cy-section-note">{t('built.governance.note')}</Notice>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="built-value-creation">
        <div className="cy-container">
          <SectionHead eyebrow={t('built.value.eyebrow')} title={t('built.value.h2')} lead={t('built.value.lead')} />
          <LensList items={valueItems} />
          <p className="cy-fineprint cy-section-note">{t('built.value.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/about#about-team">{t('built.value.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="built-confidentiality">
        <div className="cy-container">
          <SectionHead title={t('built.confidentiality.h2')} lead={t('built.confidentiality.lead')} />
          <StageList items={t('built.confidentiality.layers', { returnObjects: true })} />
          <p className="cy-body cy-section-note">
            <strong className="cy-emph">{t('built.confidentiality.notFirstLead')}</strong>{' '}
            {t('built.confidentiality.notFirst')}
          </p>
          <Notice variant="inline" className="cy-section-note">{t('built.confidentiality.note')}</Notice>
          <p className="cy-section-action">
            <Link className="cy-link" to="/privacy#privacy-disclosure">{t('built.confidentiality.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="built-process">
        <div className="cy-container">
          <SectionHead title={t('built.process.h2')} lead={t('built.process.lead')} />
          <StageList items={t('built.process.steps', { returnObjects: true })} />
          <p className="cy-fineprint cy-section-note">{t('built.process.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-stages">{t('built.process.cta')}</Link>
          </p>
        </div>
      </section>

      <PillarClose
        faqId="built-faq"
        ctaId="built-faq-cta"
        faqTitle={t('built.faq.h2')}
        faqItems={faqItems}
        idPrefix="built-faq"
        cta={t('built.faq.cta')}
        ctaTo="/submit-opportunity?type=business"
        alt={t('built.faq.alt')}
        altTo="/contact?topic=partnership#contact-form"
        siblings={t('built.faq.siblings', { returnObjects: true })}
      />
    </Layout>
  );
};

export default BuiltEnvironmentInvestments;
